import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  MinusIcon,
} from '@heroicons/react/24/outline'
import {
  AlertCountBadge,
  JournalEntryCard,
  JournalEntryDocChip,
  JournalEntryExternalLink,
  JournalEntryPostedBadge,
} from '@cottage/design-system/internal-tooling/ui'
import AccountingToolPage from '@/components/AccountingToolPage'
import UnderlineTabNav from '@/components/UnderlineTabNav'
import CredentialStatusCard from '@/components/CredentialStatusCard'
import JournalEntryPostSplitButton from '@/components/JournalEntryPostSplitButton'
import JournalEntryPreview from '@/components/JournalEntryPreview'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import SortableHeader from '@/components/SortableHeader'
import { HoverHelp } from '@/tableControls'
import WarningBanner from '@/components/WarningBanner'
import { toast } from '@/store/toastStore'
import WarehouseInventoryWorkspace from '@/components/WarehouseInventoryWorkspace'
import WarehouseUploadsPanel from '@/components/WarehouseUploadsPanel'
import {
  getActiveReconcileIgnore,
  humanCoverageStatus,
  isReconcileIgnored,
  qboMatchRowClass,
  qboMatchStatusBadge,
  qboRowReconcileMonth,
} from '@/components/qboMatchStatus'
import {
  buildUploadsMonthOptions,
  collectUploadsMonths,
  dateToReportMonth,
  defaultUploadsMonthSelection,
  matchesUploadsMonthFilter,
  pruneInvalidUploadsMonths,
} from '@/components/uploadsMonthOptions'
import {
  countBillingUploadsErrors,
  countProductUploadsErrors,
  countQboUploadsErrors,
  countWhUploadsErrors,
  sumUploadsErrorCounts,
} from '@/components/uploadsErrorCounts'
import { EMPTY_CATALOG_TABLE_FILTERS } from '@/components/catalogFilters'
import { EMPTY_LEDGER_TABLE_FILTERS } from '@/components/ledgerFilters'
import {
  saveFilterCache,
  WAREHOUSE_CATALOG_FILTER_CACHE_KEY,
  WAREHOUSE_LEDGER_FILTER_CACHE_KEY,
} from '@/utils/tableFilterCache'
import { useSortableTable } from '@/hooks/useSortableTable'
import { useWarehouseProblemStore } from '@/store/warehouseProblemStore'
import { uploadsErrorVisibleForMonth } from '@/utils/completedCalendarMonth'
import {
  concatLegacyMonthlyPreview,
  draftItemToJournalLines,
  earliestOpenOrPartialProcessMonth,
  filterDraftsByMonthAndTypes,
  getProcessMonthPostingStatus,
  hasBkJeReadinessBlocks,
  hasMonthlyJeReadinessBlocks,
  isDraftOutOfDateVsLedger,
  isLegacyMonthlyOnly,
  lineSectionsFromDraft,
  pickPreferredDraft,
  readinessLevelClass,
  resolveLegacyMonthlyDrafts,
  resolveMonthlyDraft,
  sortReadinessItems,
  sortWarehouseDrafts,
  BK_DRAFT_TYPE,
} from '@/components/warehouseDraftHelpers'
import {
  createBkInventoryDrafts,
  createWarehouseMonthDrafts,
  createWarehouseReconcileIgnore,
  deleteWarehouseAdjustment,
  deleteWarehouseBillingMonth,
  deleteWarehouseInvoice,
  deleteWarehouseReconcileIgnore,
  getWarehouseBillingAwareness,
  getWarehouseCoverage,
  getWarehouseMonthJeReadiness,
  importBrightSitesOrders,
  listWarehouseBillingLines,
  listWarehouseAdjustments,
  listWarehouseDrafts,
  listWarehouseInvoices,
  listWarehouseLayers,
  listWarehouseMovements,
  deleteWarehouseDraftFromQbo,
  pushWarehouseDraftQboUpdate,
  regenerateWarehouseDraft,
  retryNoStockMovements,
  syncWarehouseQboBills,
  updateWarehouseInvoice,
  getSkuCrosswalk,
  getInventoryManualEntries,
  upsertWarehouseReconcileNote,
  type WarehouseInvoiceUpdatePayload,
} from '@/services/warehouse'
import type {
  WarehouseBillingAwarenessResponse,
  WarehouseBillingLineItem,
  WarehouseCoverageResponse,
  WarehouseInvoiceSummary,
  WarehouseJeReadinessResponse,
  WarehouseJournalEntryDraftItem,
  WarehouseMonthAdjustmentItem,
  WarehouseMonthCoverage,
  WarehouseQboBillMatch,
  WarehouseReconcileIgnoreItem,
  WarehouseSkuCrosswalkItem,
  WarehouseInventoryManualEntryItem,
} from '@/services/warehouseTypes'
import { isBrightSitesAuthError } from '@/services/brightsitesExtension'
import {
  postJournalEntry,
  updateJournalEntryLines,
  type JournalEntryLine,
} from '@/services/accounting'
import { formatApiError } from '@/utils/apiError'
import { formatJePostedDate } from '@/pages/distribution-waterfall/waterfallJeStatus'

type WorkflowTabId = 'inventory' | 'files' | 'books' | 'warehouse_settings'

type JeSegment = 'bk' | 'month'

const WAREHOUSE_TABS: {
  id: WorkflowTabId
  label: string
}[] = [
  { id: 'inventory', label: 'Inventory' },
  { id: 'files', label: 'Uploads' },
  { id: 'books', label: 'Draft & post' },
  { id: 'warehouse_settings', label: 'Settings' },
]

const INVOICE_TYPE_OPTIONS = [
  { id: 'wh_service', label: 'WH service' },
  { id: 'bk_product', label: 'BK product' },
  { id: 'direct', label: 'Direct' },
  { id: 'mixed', label: 'Mixed' },
]


const INVOICE_TYPE_LABEL_BY_ID = Object.fromEntries(
  INVOICE_TYPE_OPTIONS.map((option) => [option.id, option.label])
)

const HALO_INVOICE_TYPE_HELP = (
  <>
    <p>What kind of Halo PDF this is:</p>
    <ul className="list-disc space-y-1.5 pl-4">
      <li>
        <strong>BK product</strong> — product delivered into the warehouse. Becomes stock when you
        confirm the delivery on Inventory.
      </li>
      <li>
        <strong>Direct</strong> — product shipped straight to a store (not through the warehouse).
      </li>
      <li>
        <strong>WH service</strong> — monthly warehouse fees. Upload with the billing file.
      </li>
      <li>
        <strong>Mixed</strong> — product and fees on one invoice. Prefer separate uploads when
        possible.
      </li>
    </ul>
  </>
)

const QBO_HOLDING_COLUMN_HELP = (
  <p>
    Amount on the QuickBooks bill coded to the warehouse clearing account (3PL Holding). Product
    and fee bills should usually land here; direct-to-store bills should not.
  </p>
)

const QBO_CHECK_HELP = (
  <p>
    Each uploaded invoice should match a QuickBooks vendor bill for the same vendor (same document
    number). Sync QBO bills after you fix coding in QuickBooks. Only bills with 3PL Holding lines
    are cached; direct-to-store invoices may show a missing bill until you Ignore them.
  </p>
)

type BadgeTone = 'warn' | 'good' | 'neutral' | 'danger'

type TabBadge =
  | { kind: 'count'; text: string; tone: BadgeTone }
  | { kind: 'dot'; tone: 'danger'; label: string }

function tabBadgeClass(tone: BadgeTone): string {
  if (tone === 'warn') return 'bg-amber-100 text-amber-800'
  if (tone === 'good') return 'bg-emerald-100 text-emerald-800'
  if (tone === 'danger') return 'bg-red-100 text-red-800'
  return 'bg-gray-200 text-gray-600'
}

function formatMoney(value: number | string | null | undefined): string {
  const num = Number(value ?? 0)
  if (!Number.isFinite(num)) return '$0.00'
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

const AMOUNT_DISPLAY_THRESHOLD = 0.005

function effectiveMetricsMatch(
  left: number | string | null | undefined,
  right: number | string | null | undefined
): boolean {
  if (!hasMetricValue(left) || !hasMetricValue(right)) return false
  const leftNum = Number(left)
  const rightNum = Number(right)
  if (!Number.isFinite(leftNum) || !Number.isFinite(rightNum)) return false
  return Math.abs(leftNum - rightNum) <= AMOUNT_DISPLAY_THRESHOLD
}

type WarehouseMatchedMetricType =
  | 'reconciled_pick_fees'
  | 'reconciled_cases'
  | 'reconciled_shipping'

const RECONCILED_METRIC_TYPES = new Set<WarehouseMatchedMetricType>([
  'reconciled_pick_fees',
  'reconciled_cases',
  'reconciled_shipping',
])

function hasMetricValue(value: number | string | null | undefined): boolean {
  return value !== null && value !== undefined && value !== ''
}

function resolvedMetric(
  value: number | string | null | undefined,
  sourceValue: number | string | null | undefined
): number | null {
  if (hasMetricValue(value)) {
    const num = Number(value)
    return Number.isFinite(num) ? num : null
  }
  if (hasMetricValue(sourceValue)) {
    const num = Number(sourceValue)
    return Number.isFinite(num) ? num : null
  }
  return null
}

function qboBillMatchSortValue(
  item: WarehouseQboBillMatch,
  key: keyof WarehouseQboBillMatch | string
) {
  switch (key) {
    case 'doc_number':
      return item.doc_number
    case 'txn_date':
      return item.txn_date ?? ''
    case 'invoice_type':
      return item.invoice_type
        ? INVOICE_TYPE_LABEL_BY_ID[item.invoice_type] ?? item.invoice_type
        : ''
    case 'vendor_name':
      return item.vendor_name ?? ''
    case 'status':
      return humanCoverageStatus(item.status)
    case 'bill_total':
      return item.bill_total != null ? Number(item.bill_total) : null
    case 'invoice_total':
      return item.invoice_total != null ? Number(item.invoice_total) : null
    case 'holding_amount':
      return item.holding_amount != null ? Number(item.holding_amount) : null
    default:
      return item[key as keyof WarehouseQboBillMatch]
  }
}

function reconcileSavedNote(
  userNotesByMonth: Record<string, string> | undefined,
  month: string
): string {
  return userNotesByMonth?.[month] ?? ''
}

function applyLocalMatchedOverrides(
  monthCoverage: WarehouseMonthCoverage,
  adjustments: WarehouseMonthAdjustmentItem[],
  reportMonth: string
): WarehouseMonthCoverage {
  const overrides = adjustments.filter(
    (adjustment) =>
      adjustment.report_month === reportMonth &&
      RECONCILED_METRIC_TYPES.has(adjustment.adjustment_type as WarehouseMatchedMetricType)
  )
  if (overrides.length === 0) return monthCoverage
  const patched: WarehouseMonthCoverage = { ...monthCoverage }
  for (const adjustment of overrides) {
    const key = adjustment.adjustment_type as keyof WarehouseMonthCoverage
    ;(patched as Record<string, unknown>)[key] = adjustment.amount
  }
  return patched
}

function getEffectiveMatchedValue(
  billingSource: number | string | null | undefined,
  invoiceSource: number | string | null | undefined,
  reconciledOverride: number | string | null | undefined
): number | null {
  if (hasMetricValue(reconciledOverride)) {
    const num = Number(reconciledOverride)
    return Number.isFinite(num) ? num : null
  }
  if (effectiveMetricsMatch(billingSource, invoiceSource)) {
    return resolvedMetric(billingSource, invoiceSource)
  }
  return null
}

function isCheck1Pass(
  monthCoverage: WarehouseMonthCoverage,
  adjustments: WarehouseMonthAdjustmentItem[],
  reportMonth: string
): boolean {
  if (monthCoverage.issues.some((issue) => issue.category === 'missing_wh_invoice')) {
    return false
  }
  const effective = applyLocalMatchedOverrides(monthCoverage, adjustments, reportMonth)
  // Prefer Halo WH invoice amounts (edited in the uploaded packages table).
  return (
    getEffectiveMatchedValue(
      monthCoverage.billing_pick_fees_source,
      monthCoverage.invoice_pick_fees_source,
      effective.reconciled_pick_fees ?? monthCoverage.invoice_pick_fees_source
    ) != null &&
    getEffectiveMatchedValue(
      monthCoverage.billing_cases_source,
      monthCoverage.invoice_cases_source,
      effective.reconciled_cases ?? monthCoverage.invoice_cases_source
    ) != null &&
    getEffectiveMatchedValue(
      monthCoverage.billing_shipping_source,
      monthCoverage.invoice_freight_source,
      effective.reconciled_shipping ?? monthCoverage.invoice_freight_source
    ) != null
  )
}

function appendReconcileIgnoreToCoverage(
  coverage: WarehouseCoverageResponse,
  payload: {
    target_type: 'qbo_bill' | 'bk_line'
    doc_number?: string
    invoice_number?: string
    invoice_line_number?: number
    split_index?: number
  },
  reportMonth: string,
  ignoreId: number
): WarehouseCoverageResponse {
  const ignore: WarehouseReconcileIgnoreItem = {
    id: ignoreId,
    scope: 'month',
    report_month: reportMonth,
  }
  if (payload.target_type === 'qbo_bill') {
    return {
      ...coverage,
      qbo_bill_matches: coverage.qbo_bill_matches.map((match) =>
        match.doc_number === payload.doc_number
          ? {
              ...match,
              reconcile_ignores: [...(match.reconcile_ignores ?? []), ignore],
            }
          : match
      ),
    }
  }
  return {
    ...coverage,
    bk_matches: coverage.bk_matches.map((match) => {
      if (
        match.invoice_number !== payload.invoice_number ||
        match.invoice_line_number !== payload.invoice_line_number ||
        (match.split_index ?? 0) !== (payload.split_index ?? 0)
      ) {
        return match
      }
      return {
        ...match,
        reconcile_ignores: [...(match.reconcile_ignores ?? []), ignore],
      }
    }),
  }
}

function removeReconcileIgnoreFromCoverage(
  coverage: WarehouseCoverageResponse,
  ignoreId: number
): WarehouseCoverageResponse {
  const strip = (ignores: WarehouseReconcileIgnoreItem[] | undefined) =>
    (ignores ?? []).filter((ignore) => ignore.id !== ignoreId)
  return {
    ...coverage,
    bk_matches: coverage.bk_matches.map((match) => ({
      ...match,
      reconcile_ignores: strip(match.reconcile_ignores),
    })),
    qbo_bill_matches: coverage.qbo_bill_matches.map((match) => ({
      ...match,
      reconcile_ignores: strip(match.reconcile_ignores),
    })),
  }
}

function ReconcileNoteField({
  systemNote,
  userNote,
  disabled,
  onCommit,
}: {
  systemNote?: string | null
  userNote: string
  disabled?: boolean
  onCommit: (note: string) => Promise<void>
}) {
  const [draft, setDraft] = useState(userNote)

  useEffect(() => {
    setDraft(userNote)
  }, [userNote])

  return (
    <div className="flex min-w-[14rem] items-center gap-1.5">
      {systemNote ? (
        <HoverHelp
          label={
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" aria-hidden />
          }
          aria-label="Validation note"
          className="inline-flex shrink-0 border-b-0"
        >
          <p>{systemNote}</p>
        </HoverHelp>
      ) : null}
      <input
        type="text"
        className="input h-8 w-full min-w-0 text-xs text-gray-700"
        value={draft}
        disabled={disabled}
        placeholder="Add a note…"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const trimmed = draft.trim()
          if (trimmed === userNote.trim()) return
          void onCommit(trimmed)
        }}
      />
    </div>
  )
}

function reportMonthLabel(reportMonth: string): string {
  const [yearStr, monthStr] = reportMonth.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  return new Date(year, month - 1, 1).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

function CardTitleWithHelp({
  title,
  helpAriaLabel,
  helpContent,
  count,
  as = 'h2',
  className = 'container-title',
}: {
  title: string
  helpAriaLabel: string
  helpContent: ReactNode
  count?: number
  as?: 'h2' | 'div'
  className?: string
  helpStyle?: 'popover' | 'hover'
}) {
  const TitleTag = as
  const titleLabel =
    count != null ? (
      <>
        {title} ({count})
      </>
    ) : (
      title
    )

  return (
    <TitleTag className={className}>
      <HoverHelp label={titleLabel} aria-label={helpAriaLabel}>
        {helpContent}
      </HoverHelp>
    </TitleTag>
  )
}

function PanelCard({
  title,
  description,
  children,
  headerAction,
  helpAriaLabel,
  helpContent,
  helpStyle = 'popover',
  className,
  headerClassName,
  contentClassName = 'px-5 py-4',
}: {
  title: string
  description?: string
  children?: ReactNode
  headerAction?: ReactNode
  helpAriaLabel?: string
  helpContent?: ReactNode
  helpStyle?: 'popover' | 'hover'
  className?: string
  headerClassName?: string
  contentClassName?: string
}) {
  return (
    <div
      className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${className ?? ''}`}
    >
      <div
        className={`flex flex-wrap items-start justify-between gap-2 border-b border-gray-200 px-5 py-3 ${headerClassName ?? ''}`}
      >
        <div className="min-w-0">
          {helpContent ? (
            <CardTitleWithHelp
              title={title}
              helpAriaLabel={helpAriaLabel ?? `${title} help`}
              helpContent={helpContent}
              helpStyle={helpStyle}
            />
          ) : (
            <h2 className="container-title">{title}</h2>
          )}
          {description ? <p className="mt-1 text-xs text-gray-500">{description}</p> : null}
        </div>
        {headerAction}
      </div>
      {children ? <div className={contentClassName}>{children}</div> : null}
    </div>
  )
}

function LoadingPlaceholder({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-sm text-gray-500">
      <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
      {message}
    </div>
  )
}

function WarehouseTabNav({
  activeTab,
  onSelect,
  badges,
}: {
  activeTab: WorkflowTabId
  onSelect: (tab: WorkflowTabId) => void
  badges: Record<WorkflowTabId, TabBadge | null>
}) {
  return (
    <div
      className="inline-flex min-w-max items-center gap-1 rounded-lg bg-gray-200 p-1"
      role="tablist"
      aria-label="Warehouse"
    >
      {WAREHOUSE_TABS.map((tab) => {
        const isActive = activeTab === tab.id
        const badge = badges[tab.id]
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(tab.id)}
            className={`flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              isActive ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
            {badge?.kind === 'dot' ? (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-red-500"
                aria-label={badge.label}
                title={badge.label}
              />
            ) : null}
            {badge?.kind === 'count' ? (
              <span
                className={`whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tabBadgeClass(badge.tone)}`}
              >
                {badge.text}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

function showActionWarnings(warnings: string[]) {
  for (const warning of warnings) {
    toast.warning(warning)
  }
}

export default function Warehouse() {
  const [activeTab, setActiveTab] = useState<WorkflowTabId>('inventory')
  const [loading, setLoading] = useState(false)
  const [coverage, setCoverage] = useState<WarehouseCoverageResponse | null>(null)
  const [invoices, setInvoices] = useState<WarehouseInvoiceSummary[]>([])
  const [billingLines, setBillingLines] = useState<WarehouseBillingLineItem[]>([])
  const [layers, setLayers] = useState<Awaited<ReturnType<typeof listWarehouseLayers>>>([])
  const [movements, setMovements] = useState<Awaited<ReturnType<typeof listWarehouseMovements>>>([])
  const [drafts, setDrafts] = useState<WarehouseJournalEntryDraftItem[]>([])
  const [activeDraftId, setActiveDraftId] = useState<number | null>(null)
  const [draftLines, setDraftLines] = useState<JournalEntryLine[]>([])
  const [draftsLoading, setDraftsLoading] = useState(false)
  const [draftActionId, setDraftActionId] = useState<number | null>(null)
  const [postedJeMenuOpen, setPostedJeMenuOpen] = useState(false)
  const [draftJeMenuOpen, setDraftJeMenuOpen] = useState(false)
  const [jeReadiness, setJeReadiness] = useState<WarehouseJeReadinessResponse | null>(null)
  const [readinessLoading, setReadinessLoading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [packageYear, setPackageYear] = useState('')
  const [packageMonthPart, setPackageMonthPart] = useState('')
  const packageMonth =
    packageYear && packageMonthPart ? `${packageYear}-${packageMonthPart}` : ''
  const [skuCrosswalkRows, setSkuCrosswalkRows] = useState<WarehouseSkuCrosswalkItem[]>([])
  const [uploadsSelectedMonths, setUploadsSelectedMonths] = useState<string[]>([])
  const uploadsMonthsInitializedRef = useRef(false)
  const [reconcileRefreshing, setReconcileRefreshing] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState('')
  const [adjustments, setAdjustments] = useState<WarehouseMonthAdjustmentItem[]>([])
  const [showIgnoredQbo, setShowIgnoredQbo] = useState(false)
  const [authRecovery, setAuthRecovery] = useState(false)
  const [jeSegment, setJeSegment] = useState<JeSegment>('bk')
  const [previewLineSections, setPreviewLineSections] = useState<string[] | undefined>()
  const [legacyMonthlyView, setLegacyMonthlyView] = useState(false)
  const [readinessChecklistCollapsed, setReadinessChecklistCollapsed] = useState(true)
  const [manualEntries, setManualEntries] = useState<WarehouseInventoryManualEntryItem[]>([])
  const manualEntriesRef = useRef(manualEntries)
  manualEntriesRef.current = manualEntries
  const [inventoryViewMode, setInventoryViewMode] = useState<
    'ledger' | 'flow' | 'catalog' | 'vendors'
  >('ledger')
  const [catalogSkuFilterRequest, setCatalogSkuFilterRequest] = useState<{
    sku: string
    nonce: number
  } | null>(null)
  const [focusProductInvoiceLineRequest, setFocusProductInvoiceLineRequest] = useState<{
    id: number
    nonce: number
  } | null>(null)
  const [ledgerBkTxnFilterRequest, setLedgerBkTxnFilterRequest] = useState<{
    bkTxnId: string
    nonce: number
  } | null>(null)
  const [pendingProgressionKeys, setPendingProgressionKeys] = useState<Set<string>>(
    () => new Set()
  )
  const noStockAutoRetryKeyRef = useRef<string | null>(null)

  // Single month drives inventory, files matching, and allocate.
  const processMonth = selectedMonth
  const selectedReconcileMonth = packageMonth

  const activeDraft = useMemo(
    () => drafts.find((draft) => draft.id === activeDraftId) ?? null,
    [drafts, activeDraftId]
  )
  const activeDraftTxnDateLabel = formatJePostedDate(activeDraft?.txn_date ?? null)

  useEffect(() => {
    setPostedJeMenuOpen(false)
    setDraftJeMenuOpen(false)
  }, [activeDraftId])

  const processMonthHasJeDrafts = useMemo(() => {
    if (!processMonth) return false
    if (filterDraftsByMonthAndTypes(drafts, processMonth, [BK_DRAFT_TYPE]).length > 0) {
      return true
    }
    if (resolveMonthlyDraft(drafts, processMonth)) return true
    if (isLegacyMonthlyOnly(drafts, processMonth)) {
      return resolveLegacyMonthlyDrafts(drafts, processMonth).length > 0
    }
    return false
  }, [drafts, processMonth])

  const balanced = useMemo(() => {
    const debits = draftLines
      .filter((line) => line.posting_type === 'Debit')
      .reduce((sum, line) => sum + Number(line.amount ?? 0), 0)
    const credits = draftLines
      .filter((line) => line.posting_type === 'Credit')
      .reduce((sum, line) => sum + Number(line.amount ?? 0), 0)
    return Math.abs(debits - credits) < 0.005
  }, [draftLines])

  const billingMonthSummaries = useMemo(() => {
    const byMonth = new Map<
      string,
      { lineCount: number; caseCount: number; isLegacy: boolean }
    >()
    for (const line of billingLines) {
      const existing = byMonth.get(line.report_month) ?? {
        lineCount: 0,
        caseCount: 0,
        isLegacy: line.is_legacy,
      }
      existing.lineCount += 1
      existing.caseCount += Number(line.item_qty ?? 0)
      byMonth.set(line.report_month, existing)
    }
    return Array.from(byMonth.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([month, stats]) => ({
        month,
        ...stats,
        whInvoice:
          invoices.find(
            (invoice) =>
              invoice.report_month === month &&
              (invoice.invoice_type === 'wh_service' || invoice.invoice_type === 'mixed')
          ) ?? null,
      }))
  }, [billingLines, invoices])

  const processMonthFeeTotals = useMemo(() => {
    const whInvoices = invoices.filter(
      (invoice) =>
        invoice.report_month === processMonth &&
        (invoice.invoice_type === 'wh_service' || invoice.invoice_type === 'mixed')
    )
    return {
      storageFee: whInvoices.reduce((sum, invoice) => sum + (invoice.storage_fee_total ?? 0), 0),
      adminFee: whInvoices.reduce((sum, invoice) => sum + (invoice.admin_fee_total ?? 0), 0),
      hasWhInvoice: whInvoices.length > 0,
    }
  }, [invoices, processMonth])

  const sortedReconcileMonths = useMemo(() => {
    const months = billingMonthSummaries.map((summary) => summary.month)
    months.sort()
    return months
  }, [billingMonthSummaries])

  const orphanAdjustments = useMemo(() => {
    const billingMonths = new Set(billingMonthSummaries.map((summary) => summary.month))
    return adjustments.filter((adjustment) => !billingMonths.has(adjustment.report_month))
  }, [adjustments, billingMonthSummaries])

  useEffect(() => {
    // Inventory / Draft & post month: only uploaded billing periods.
    if (sortedReconcileMonths.length === 0) {
      if (selectedMonth) setSelectedMonth('')
      return
    }
    if (sortedReconcileMonths.includes(selectedMonth)) return
    if (draftsLoading) return

    const preferred =
      earliestOpenOrPartialProcessMonth(sortedReconcileMonths, drafts) ??
      sortedReconcileMonths.at(-1) ??
      ''
    if (!preferred) return
    setSelectedMonth(preferred)
  }, [sortedReconcileMonths, selectedMonth, drafts, draftsLoading])

  const processMonthOptions = useMemo(
    () =>
      [...sortedReconcileMonths].reverse().map((month) => ({
        id: month,
        label: reportMonthLabel(month),
        status: getProcessMonthPostingStatus(drafts, month),
      })),
    [sortedReconcileMonths, drafts]
  )

  useEffect(() => {
    if (!selectedMonth || sortedReconcileMonths.length === 0) return
    if (!sortedReconcileMonths.includes(selectedMonth)) return
    if (packageMonth === selectedMonth) return
    if (packageMonth && sortedReconcileMonths.includes(packageMonth)) return
    const [year, monthPart] = selectedMonth.split('-')
    if (year && monthPart) {
      setPackageYear(year)
      setPackageMonthPart(monthPart)
    }
  }, [selectedMonth, sortedReconcileMonths, packageMonth])

  const allQboMatches = useMemo(
    () => coverage?.qbo_bill_matches ?? [],
    [coverage]
  )

  const uploadsMonthList = useMemo(
    () =>
      collectUploadsMonths({
        whReportMonths: invoices
          .filter(
            (invoice) =>
              invoice.invoice_type === 'wh_service' ||
              (invoice.invoice_type === 'mixed' && invoice.report_month)
          )
          .map((invoice) => invoice.report_month),
        productInvoiceDates: invoices
          .filter((invoice) => {
            if (invoice.invoice_type === 'wh_service') return false
            return !(invoice.report_month && invoice.invoice_type === 'mixed')
          })
          .map((invoice) => invoice.invoice_date),
        billingReportMonths: billingLines.map((line) => line.report_month),
        qboTxnDates: allQboMatches.map((match) => match.txn_date),
      }),
    [invoices, billingLines, allQboMatches]
  )

  const uploadsMonthOptions = useMemo(
    () => buildUploadsMonthOptions(uploadsMonthList),
    [uploadsMonthList]
  )

  useEffect(() => {
    if (uploadsMonthList.length === 0) {
      setUploadsSelectedMonths([])
      return
    }

    setUploadsSelectedMonths((current) => {
      const valid = pruneInvalidUploadsMonths(current, uploadsMonthList)
      if (valid.length > 0) return valid
      if (!uploadsMonthsInitializedRef.current) {
        return defaultUploadsMonthSelection(uploadsMonthList)
      }
      if (current.length > 0) {
        return defaultUploadsMonthSelection(uploadsMonthList)
      }
      return []
    })
    uploadsMonthsInitializedRef.current = true
  }, [uploadsMonthList])

  const filteredQboMatches = useMemo(
    () =>
      allQboMatches.filter((match) =>
        matchesUploadsMonthFilter(dateToReportMonth(match.txn_date), uploadsSelectedMonths)
      ),
    [allQboMatches, uploadsSelectedMonths]
  )

  const qboMismatches = useMemo(
    () =>
      filteredQboMatches.filter((match) => {
        const rowMonth = qboRowReconcileMonth(match, selectedReconcileMonth)
        return (
          match.status !== 'matched' &&
          !isReconcileIgnored(match.reconcile_ignores, rowMonth)
        )
      }),
    [filteredQboMatches, selectedReconcileMonth]
  )
  const qboMatched = useMemo(
    () =>
      filteredQboMatches.filter((match) => {
        const rowMonth = qboRowReconcileMonth(match, selectedReconcileMonth)
        return (
          match.status === 'matched' &&
          !isReconcileIgnored(match.reconcile_ignores, rowMonth)
        )
      }),
    [filteredQboMatches, selectedReconcileMonth]
  )
  const qboIgnoredRows = useMemo(
    () =>
      filteredQboMatches.filter((match) =>
        isReconcileIgnored(
          match.reconcile_ignores,
          qboRowReconcileMonth(match, selectedReconcileMonth)
        )
      ),
    [filteredQboMatches, selectedReconcileMonth]
  )
  const qboDisplayRows = useMemo(() => {
    const active = filteredQboMatches.filter(
      (match) =>
        !isReconcileIgnored(
          match.reconcile_ignores,
          qboRowReconcileMonth(match, selectedReconcileMonth)
        )
    )
    const mismatches = active.filter((match) => match.status !== 'matched')
    const matched = active.filter((match) => match.status === 'matched')
    const visible = [...mismatches, ...matched]
    return showIgnoredQbo ? [...visible, ...qboIgnoredRows] : visible
  }, [filteredQboMatches, showIgnoredQbo, qboIgnoredRows, selectedReconcileMonth])
  const {
    sortedData: sortedQboDisplayRows,
    requestSort: requestQboSort,
    getSortDirection: getQboSortDirection,
  } = useSortableTable(
    qboDisplayRows,
    { key: 'txn_date', direction: 'asc' },
    qboBillMatchSortValue
  )
  const check3NeedsSync = !coverage?.halo_bills_synced_at

  // QBO bill check is advisory QA — Confirm & process only depends on Check 1 tying out.

  const processMonthCoverage = useMemo(() => {
    if (!coverage || !processMonth) return null
    return coverage.months.find((month) => month.report_month === processMonth) ?? null
  }, [coverage, processMonth])

  const processMonthReconcileReady = useMemo(() => {
    if (!processMonthCoverage || !processMonth) return false
    return isCheck1Pass(processMonthCoverage, adjustments, processMonth)
  }, [processMonthCoverage, adjustments, processMonth])

  const canCreateBkDrafts = !hasBkJeReadinessBlocks(jeReadiness?.items) && !readinessLoading

  const canCreateMonthDrafts = Boolean(
    processMonth &&
      processMonthReconcileReady &&
      !readinessLoading &&
      jeReadiness &&
      !hasMonthlyJeReadinessBlocks(jeReadiness.items)
  )

  const canCreateJeDrafts = Boolean(processMonth) && (canCreateBkDrafts || canCreateMonthDrafts)

  const sortedReadinessItems = useMemo(
    () => (jeReadiness ? sortReadinessItems(jeReadiness.items) : []),
    [jeReadiness]
  )

  const [billingAwareness, setBillingAwareness] = useState<WarehouseBillingAwarenessResponse | null>(
    null
  )

  useEffect(() => {
    const months = Array.from(new Set(billingLines.map((line) => line.report_month))).sort()
    if (months.length === 0) {
      setBillingAwareness(null)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const results = await Promise.all(months.map((month) => getWarehouseBillingAwareness(month)))
        if (!cancelled) {
          setBillingAwareness({
            report_month: months.length === 1 ? (months[0] ?? '') : '',
            mismatch_count: results.reduce(
              (sum, result) =>
                uploadsErrorVisibleForMonth(result.report_month || null)
                  ? sum + result.mismatch_count
                  : sum,
              0
            ),
            rows: results.flatMap((result) => result.rows),
          })
        }
      } catch {
        if (!cancelled) setBillingAwareness(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [billingLines])

  const billingQtyErrorCount = billingAwareness?.mismatch_count ?? 0

  const uploadsErrorCounts = useMemo(() => {
    const wh = countWhUploadsErrors(invoices, billingLines)
    const product = countProductUploadsErrors(invoices)
    const qbo = countQboUploadsErrors(allQboMatches)
    return {
      wh,
      product,
      billing: countBillingUploadsErrors(billingLines, billingQtyErrorCount),
      qbo,
    }
  }, [invoices, billingLines, allQboMatches, billingQtyErrorCount])

  const uploadsErrorTotal = useMemo(
    () => sumUploadsErrorCounts(uploadsErrorCounts),
    [uploadsErrorCounts]
  )

  const progressionProblemCount = useMemo(
    () => movements.filter((move) => Boolean(move.exception?.trim())).length,
    [movements]
  )

  const fixableNoStockRetryKey = useMemo(() => {
    const skusWithStock = new Set(
      layers
        .filter((layer) => (Number(layer.qty_remaining) || 0) > 0)
        .map((layer) => layer.bulk_sku.trim().toLowerCase())
        .filter(Boolean)
    )
    if (skusWithStock.size === 0) return ''
    const fixableKeys = movements
      .filter((move) => {
        if (!move.exception?.trim()) return false
        const isNoStock = /No inventory layers for/i.test(move.exception)
        const isShortfall = /Insufficient inventory for/i.test(move.exception)
        if (!isNoStock && !isShortfall) return false
        const fromNoStock = move.exception.match(/No inventory layers for\s+(.+)$/i)?.[1]?.trim()
        const fromShortfall = move.exception
          .match(/Insufficient inventory for\s+(.+?)\s+\(short/i)?.[1]
          ?.trim()
        const candidates = [move.bulk_sku, move.wh_sku, fromNoStock, fromShortfall]
          .map((value) => (value ?? '').trim().toLowerCase())
          .filter(Boolean)
        return candidates.some((sku) => skusWithStock.has(sku))
      })
      .map((move) => move.move_key)
      .sort()
    return fixableKeys.join('|')
  }, [layers, movements])

  const setWarehouseProblemCount = useWarehouseProblemStore((s) => s.setProblemCount)
  const setUploadsErrorCount = useWarehouseProblemStore((s) => s.setUploadsErrorCount)
  const ledgerErrorCount = useWarehouseProblemStore((s) => s.ledgerErrorCount)

  useEffect(() => {
    setWarehouseProblemCount(progressionProblemCount)
  }, [progressionProblemCount, setWarehouseProblemCount])

  useEffect(() => {
    setUploadsErrorCount(uploadsErrorTotal)
  }, [uploadsErrorTotal, setUploadsErrorCount])

  // Opening stock can be saved after shipments were recorded with stock exceptions
  // ("No inventory layers" or "Insufficient inventory"). Those movement rows stay
  // broken until deleted and re-processed — do that automatically once layers with
  // remaining qty exist for the affected SKUs.
  useEffect(() => {
    if (activeTab !== 'inventory' || loading) return
    if (!fixableNoStockRetryKey) return
    if (noStockAutoRetryKeyRef.current === fixableNoStockRetryKey) return
    noStockAutoRetryKeyRef.current = fixableNoStockRetryKey
    let cancelled = false
    void (async () => {
      try {
        const result = await retryNoStockMovements()
        if (cancelled) return
        if (result.movements_retried > 0) {
          await refreshAll({ silent: true })
          if (cancelled) return
          toast.success(
            `Re-recorded ${result.movements_retried} store shipment(s) now that opening stock is available.`
          )
          if (result.warnings.length > 0) showActionWarnings(result.warnings)
        }
      } catch (err) {
        if (cancelled) return
        noStockAutoRetryKeyRef.current = null
        toast.error(formatApiError(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeTab, fixableNoStockRetryKey, loading])

  const tabBadges = useMemo<Record<WorkflowTabId, TabBadge | null>>(() => {
    const inventoryAlertParts: string[] = []
    if (progressionProblemCount > 0) {
      inventoryAlertParts.push(
        progressionProblemCount === 1
          ? '1 inventory problem outstanding'
          : `${progressionProblemCount} inventory problems outstanding`
      )
    }
    if (ledgerErrorCount > 0) {
      inventoryAlertParts.push(
        ledgerErrorCount === 1
          ? '1 ledger row with errors'
          : `${ledgerErrorCount} ledger rows with errors`
      )
    }

    return {
      inventory:
        inventoryAlertParts.length > 0
          ? {
              kind: 'dot',
              tone: 'danger',
              label: inventoryAlertParts.join('; '),
            }
          : null,
      files:
        uploadsErrorTotal > 0
          ? {
              kind: 'dot',
              tone: 'danger',
              label:
                uploadsErrorTotal === 1
                  ? '1 uploads error'
                  : `${uploadsErrorTotal} uploads errors`,
            }
          : null,
      books: null,
      warehouse_settings: null,
    }
  }, [progressionProblemCount, ledgerErrorCount, uploadsErrorTotal])

  const skuDisplayNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const row of skuCrosswalkRows) {
      if (!row.is_active) continue
      map.set(row.bk_sku, row.display_name)
    }
    return map
  }, [skuCrosswalkRows])

  function handleOpenCatalogSku(sku: string) {
    const trimmed = sku.trim()
    if (!trimmed) return
    saveFilterCache(WAREHOUSE_CATALOG_FILTER_CACHE_KEY, {
      ...EMPTY_CATALOG_TABLE_FILTERS,
      bk_sku: [trimmed],
    })
    setCatalogSkuFilterRequest({ sku: trimmed, nonce: Date.now() })
    setActiveTab('inventory')
    setInventoryViewMode('catalog')
  }

  function handleJumpToProductInvoice(productInvoiceLineId: number) {
    setFocusProductInvoiceLineRequest({ id: productInvoiceLineId, nonce: Date.now() })
    setActiveTab('files')
  }

  function handleJumpToLedgerTxn(bkTxnId: string) {
    const trimmed = bkTxnId.trim()
    if (!trimmed) return
    saveFilterCache(WAREHOUSE_LEDGER_FILTER_CACHE_KEY, {
      ...EMPTY_LEDGER_TABLE_FILTERS,
      bk_txn_id: [trimmed],
    })
    setLedgerBkTxnFilterRequest({ bkTxnId: trimmed, nonce: Date.now() })
    setActiveTab('inventory')
    setInventoryViewMode('ledger')
  }

  const handleSkuCrosswalkSaved = useCallback((rows: WarehouseSkuCrosswalkItem[]) => {
    setSkuCrosswalkRows(rows)
    void listWarehouseInvoices()
      .then(setInvoices)
      .catch((err) => toast.error(formatApiError(err)))
    void useWarehouseProblemStore.getState().fetchLedgerErrorCount()
  }, [])

  const selectDraft = useCallback(
    (draft: WarehouseJournalEntryDraftItem | null) => {
      if (!draft) {
        setActiveDraftId(null)
        setDraftLines([])
        return
      }
      setActiveDraftId(draft.id)
      setDraftLines(draftItemToJournalLines(draft))
    },
    []
  )

  const markDraftPendingRepost = useCallback((draftId: number) => {
    setDrafts((prev) =>
      prev.map((item) => {
        if (item.id !== draftId) return item
        const sourceData = item.source_data ? { ...item.source_data } : undefined
        if (sourceData) {
          delete sourceData.needs_qbo_update
          delete sourceData.ledger_content_dirty
        }
        return {
          ...item,
          status: 'draft',
          posted_at: null,
          source_data: sourceData,
        }
      })
    )
  }, [])

  const refreshDrafts = useCallback(
    async (preferredId?: number | null) => {
      setDraftsLoading(true)
      try {
        const result = await listWarehouseDrafts()
        const sorted = sortWarehouseDrafts(result.drafts)
        setDrafts(sorted)
        if (preferredId != null) {
          const preferred = sorted.find((draft) => draft.id === preferredId)
          if (preferred) {
            selectDraft(preferred)
            const draftType = preferred.draft_type ?? preferred.source_data?.draft_type
            if (draftType === 'bk') setJeSegment('bk')
            else setJeSegment('month')
          }
        }
      } catch (err) {
        toast.error(formatApiError(err))
      } finally {
        setDraftsLoading(false)
      }
    },
    [selectDraft]
  )

  useEffect(() => {
    if (!processMonth) {
      selectDraft(null)
      setPreviewLineSections(undefined)
      setLegacyMonthlyView(false)
      return
    }

    if (jeSegment === 'bk') {
      const draft = pickPreferredDraft(
        filterDraftsByMonthAndTypes(drafts, processMonth, [BK_DRAFT_TYPE])
      )
      selectDraft(draft)
      setPreviewLineSections(undefined)
      setLegacyMonthlyView(false)
      return
    }

    const combined = resolveMonthlyDraft(drafts, processMonth)
    if (combined) {
      selectDraft(combined)
      setPreviewLineSections(lineSectionsFromDraft(combined))
      setLegacyMonthlyView(false)
      return
    }

    if (isLegacyMonthlyOnly(drafts, processMonth)) {
      const legacy = resolveLegacyMonthlyDrafts(drafts, processMonth)
      const preview = concatLegacyMonthlyPreview(legacy)
      setActiveDraftId(null)
      setDraftLines(preview.lines)
      setPreviewLineSections(preview.lineSections)
      setLegacyMonthlyView(true)
      return
    }

    selectDraft(null)
    setPreviewLineSections(undefined)
    setLegacyMonthlyView(false)
  }, [drafts, processMonth, jeSegment, selectDraft])

  async function refreshAll(options?: { silent?: boolean }): Promise<{
    layers: Awaited<ReturnType<typeof listWarehouseLayers>>
    movements: Awaited<ReturnType<typeof listWarehouseMovements>>
  } | null> {
    if (!options?.silent) setLoading(true)
    const results = await Promise.allSettled([
      getWarehouseCoverage(),
      listWarehouseInvoices(),
      listWarehouseBillingLines(),
      listWarehouseLayers(),
      listWarehouseMovements(),
      listWarehouseAdjustments(),
      getSkuCrosswalk(),
      getInventoryManualEntries(),
    ])
    const errors: string[] = []
    if (results[0].status === 'fulfilled') {
      setCoverage(results[0].value)
    } else {
      errors.push(formatApiError(results[0].reason))
      if (isBrightSitesAuthError(results[0].reason)) setAuthRecovery(true)
    }
    if (results[1].status === 'fulfilled') setInvoices(results[1].value)
    else errors.push(formatApiError(results[1].reason))
    if (results[2].status === 'fulfilled') setBillingLines(results[2].value)
    else errors.push(formatApiError(results[2].reason))
    let nextLayers: Awaited<ReturnType<typeof listWarehouseLayers>> | null = null
    let nextMovements: Awaited<ReturnType<typeof listWarehouseMovements>> | null = null
    if (results[3].status === 'fulfilled') {
      nextLayers = results[3].value
      setLayers(nextLayers)
    } else {
      errors.push(formatApiError(results[3].reason))
    }
    if (results[4].status === 'fulfilled') {
      nextMovements = results[4].value
      setMovements(nextMovements)
    } else {
      errors.push(formatApiError(results[4].reason))
    }
    if (results[5].status === 'fulfilled') setAdjustments(results[5].value)
    else errors.push(formatApiError(results[5].reason))
    if (results[6].status === 'fulfilled') setSkuCrosswalkRows(results[6].value.rows)
    else errors.push(formatApiError(results[6].reason))
    if (results[7].status === 'fulfilled') setManualEntries(results[7].value.rows)
    else errors.push(formatApiError(results[7].reason))
    if (errors.length > 0) toast.error(errors.join(' · '))
    else setAuthRecovery(false)
    if (!options?.silent) setLoading(false)
    void useWarehouseProblemStore.getState().fetchLedgerErrorCount()
    if (!nextLayers || !nextMovements) return null
    return { layers: nextLayers, movements: nextMovements }
  }

  function confirmPendingProgressionKeys(keys: string[]) {
    setPendingProgressionKeys((current) => {
      const next = new Set(current)
      for (const key of keys) next.delete(key)
      return next
    })
  }

  useEffect(() => {
    void refreshAll()
    void refreshDrafts()
    // Initial load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (activeTab !== 'books') return
    void refreshDrafts()
    // Refresh when opening Draft & post.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== 'books' || !processMonth) {
      setJeReadiness(null)
      return
    }
    let cancelled = false
    setReadinessLoading(true)
    void getWarehouseMonthJeReadiness(processMonth)
      .then((result) => {
        if (!cancelled) setJeReadiness(result)
      })
      .catch((err) => {
        if (!cancelled) toast.error(formatApiError(err))
      })
      .finally(() => {
        if (!cancelled) setReadinessLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [activeTab, processMonth])

  useEffect(() => {
    if (!jeReadiness || jeReadiness.report_month !== processMonth) return
    setReadinessChecklistCollapsed(
      jeReadiness.warn_count === 0 && jeReadiness.block_count === 0
    )
  }, [processMonth, jeReadiness])

  async function handleRefresh() {
    setLoading(true)
    try {
      await importBrightSitesOrders()
      await refreshAll()
    } catch (err) {
      toast.error(formatApiError(err))
      if (isBrightSitesAuthError(err)) {
        setAuthRecovery(true)
      }
      setLoading(false)
    }
  }

  async function handleCreateJeDrafts() {
    if (!processMonth) {
      toast.error('Select a process month before creating drafts')
      return
    }

    setLoading(true)
    const errors: string[] = []
    let preferredId: number | null = null
    let createdAny = false
    let postedNeedsQboUpdate = false
    try {
      try {
        const bkResult = await createBkInventoryDrafts({
          report_month: processMonth,
          overwrite_posted: true,
        })
        preferredId = bkResult[0]?.draft_id ?? preferredId
        createdAny = createdAny || bkResult.length > 0
        postedNeedsQboUpdate =
          postedNeedsQboUpdate ||
          bkResult.some((draft) => Boolean(draft.needs_qbo_update))
      } catch (err) {
        errors.push(formatApiError(err))
      }

      try {
        const monthResult = await createWarehouseMonthDrafts({
          report_month: processMonth,
          storage_fee: processMonthFeeTotals.storageFee,
          admin_fee: processMonthFeeTotals.adminFee,
          overwrite_posted: true,
        })
        // Prefer BK in the segmented control after create; only fall back to month if BK missing.
        preferredId = preferredId ?? monthResult.month_draft?.draft_id ?? null
        createdAny = createdAny || Boolean(monthResult.month_draft)
        if (monthResult.month_draft?.needs_qbo_update) {
          postedNeedsQboUpdate = true
        }
        if (monthResult.warnings.length > 0) showActionWarnings(monthResult.warnings)
      } catch (err) {
        errors.push(formatApiError(err))
      }

      await refreshDrafts(preferredId)
      setReadinessChecklistCollapsed(true)

      if (createdAny) {
        if (postedNeedsQboUpdate) {
          toast.success(
            'Drafts updated in Bookkeeper. Use Update Existing JE on the Posted control to sync QuickBooks.'
          )
        } else {
          toast.success('Journal entry drafts created')
        }
        if (errors.length > 0) {
          toast.error(errors.join(' · '))
        }
      } else {
        toast.error(errors.join(' · ') || 'No journal entry drafts were created')
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleInvoiceFieldCommit(
    invoiceId: number,
    payload: WarehouseInvoiceUpdatePayload
  ) {
    try {
      await updateWarehouseInvoice(invoiceId, payload)
      await refreshAll({ silent: true })
    } catch (err) {
      toast.error(formatApiError(err))
    }
  }

  async function handleRegenerateDraft(draftId: number) {
    setDraftActionId(draftId)
    try {
      const result = await regenerateWarehouseDraft(draftId)
      await refreshDrafts(result.draft.id)
      toast.success('Draft regenerated from ledger')
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setDraftActionId(null)
    }
  }

  async function handleRegeneratePostedDraft(draftId: number) {
    const draft = drafts.find((item) => item.id === draftId)
    const confirmed = window.confirm(
      draft?.doc_number
        ? `Overwrite ${draft.doc_number} from the current ledger?`
        : 'Overwrite this journal entry from the current ledger?'
    )
    if (!confirmed) return
    if (!processMonth) {
      toast.error('Select a process month before regenerating')
      return
    }

    setDraftActionId(draftId)
    setPostedJeMenuOpen(false)
    markDraftPendingRepost(draftId)
    try {
      let preferredId = draftId
      if (jeSegment === 'bk') {
        const bkResult = await createBkInventoryDrafts({
          report_month: processMonth,
          overwrite_posted: true,
        })
        preferredId = bkResult[0]?.draft_id ?? preferredId
      } else {
        const monthResult = await createWarehouseMonthDrafts({
          report_month: processMonth,
          storage_fee: processMonthFeeTotals.storageFee,
          admin_fee: processMonthFeeTotals.adminFee,
          overwrite_posted: true,
        })
        preferredId = monthResult.month_draft?.draft_id ?? preferredId
        if (monthResult.warnings.length > 0) showActionWarnings(monthResult.warnings)
      }
      await refreshDrafts(preferredId)
      toast.success('Draft regenerated from ledger')
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setDraftActionId(null)
    }
  }

  async function handlePushQboUpdate(draftId: number) {
    const draft = drafts.find((item) => item.id === draftId)
    const confirmed = window.confirm(
      draft?.doc_number
        ? `Rebuild ${draft.doc_number} from the current ledger and overwrite the existing journal entry in QuickBooks?`
        : 'Rebuild this journal entry from the current ledger and overwrite the existing entry in QuickBooks?'
    )
    if (!confirmed) return

    setDraftActionId(draftId)
    setPostedJeMenuOpen(false)
    try {
      const result = await pushWarehouseDraftQboUpdate(draftId, {
        force_regenerate: true,
      })
      await refreshDrafts(result.draft.id)
      toast.success(
        result.regenerated
          ? 'Regenerated draft and updated QuickBooks'
          : 'Updated journal entry in QuickBooks'
      )
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setDraftActionId(null)
    }
  }

  function willUncheckWarehouse3plReadiness(draftId: number): boolean {
    if (!processMonth) return false
    const bkDraft = pickPreferredDraft(
      filterDraftsByMonthAndTypes(drafts, processMonth, [BK_DRAFT_TYPE])
    )
    const monthDraft = resolveMonthlyDraft(drafts, processMonth)
    const bothPosted =
      bkDraft?.status === 'posted' && monthDraft?.status === 'posted'
    if (!bothPosted) return false
    return bkDraft?.id === draftId || monthDraft?.id === draftId
  }

  async function handleDeleteFromQbo(draftId: number) {
    const draft = drafts.find((item) => item.id === draftId)
    const baseConfirm = draft?.doc_number
      ? `Delete ${draft.doc_number} from QuickBooks?`
      : 'Delete this journal entry from QuickBooks?'
    const readinessSuffix = willUncheckWarehouse3plReadiness(draftId)
      ? ' This will also uncheck the 3PL warehouse expenses allocated income allocation readiness item.'
      : ''
    const confirmed = window.confirm(`${baseConfirm}${readinessSuffix}`)
    if (!confirmed) return

    setDraftActionId(draftId)
    setPostedJeMenuOpen(false)
    try {
      const result = await deleteWarehouseDraftFromQbo(draftId)
      await refreshDrafts(result.draft.id)
      await refreshAll({ silent: true })
      toast.success('Journal entry deleted from QuickBooks')
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setDraftActionId(null)
    }
  }

  async function handleSyncQboBills() {
    setLoading(true)
    try {
      const result = await syncWarehouseQboBills()
      toast.success(result.message)
      await refreshAll()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  async function handleDeleteAdjustment(adjustmentId: number) {
    if (!confirm('Delete this manual adjustment?')) return
    setLoading(true)
    try {
      await deleteWarehouseAdjustment(adjustmentId)
      await refreshAll()
    } catch (err) {
      toast.error(formatApiError(err))
      setLoading(false)
    }
  }

  async function handleReconcileIgnore(payload: {
    target_type: 'qbo_bill' | 'bk_line'
    doc_number?: string
    invoice_number?: string
    invoice_line_number?: number
    split_index?: number
    report_month?: string
  }) {
    const reportMonth = payload.report_month ?? selectedReconcileMonth
    if (!reportMonth) {
      toast.error('Select a reconcile month before ignoring.')
      return
    }
    const previousCoverage = coverage
    const tempIgnoreId = -Date.now()
    setCoverage((prev) =>
      prev
        ? appendReconcileIgnoreToCoverage(
            prev,
            payload,
            reportMonth,
            tempIgnoreId
          )
        : prev
    )
    setReconcileRefreshing(true)
    try {
      await createWarehouseReconcileIgnore({
        target_type: payload.target_type,
        scope: 'month',
        doc_number: payload.doc_number,
        invoice_number: payload.invoice_number,
        invoice_line_number: payload.invoice_line_number,
        split_index: payload.split_index ?? 0,
        report_month: reportMonth,
      })
      await refreshAll({ silent: true })
    } catch (err) {
      setCoverage(previousCoverage)
      toast.error(formatApiError(err))
    } finally {
      setReconcileRefreshing(false)
    }
  }

  async function handleReconcileUnignore(ignoreId: number) {
    const previousCoverage = coverage
    setCoverage((prev) => (prev ? removeReconcileIgnoreFromCoverage(prev, ignoreId) : prev))
    setReconcileRefreshing(true)
    try {
      await deleteWarehouseReconcileIgnore(ignoreId)
      await refreshAll({ silent: true })
    } catch (err) {
      setCoverage(previousCoverage)
      toast.error(formatApiError(err))
    } finally {
      setReconcileRefreshing(false)
    }
  }

  async function handleReconcileNoteSave(
    payload: {
      target_type: 'qbo_bill' | 'bk_line'
      doc_number?: string
      invoice_number?: string
      invoice_line_number?: number
      split_index?: number
      report_month?: string
    },
    note: string
  ) {
    const reportMonth = payload.report_month ?? selectedReconcileMonth
    if (!reportMonth) {
      toast.error('Select a reconcile month before saving a note.')
      return
    }
    setReconcileRefreshing(true)
    try {
      await upsertWarehouseReconcileNote({
        target_type: payload.target_type,
        report_month: reportMonth,
        doc_number: payload.doc_number,
        invoice_number: payload.invoice_number,
        invoice_line_number: payload.invoice_line_number,
        split_index: payload.split_index ?? 0,
        note,
      })
      await refreshAll({ silent: true })
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setReconcileRefreshing(false)
    }
  }

  async function handleDeleteInvoice(id: number) {
    if (!confirm('Delete this uploaded invoice?')) return
    setLoading(true)
    try {
      await deleteWarehouseInvoice(id)
      await refreshAll()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  async function handleDeleteBillingMonth(month: string) {
    if (!confirm(`Delete all billing lines for ${month}?`)) return
    setLoading(true)
    try {
      await deleteWarehouseBillingMonth(month)
      await refreshAll()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  async function handlePostDraft() {
    if (!activeDraftId || !balanced) return
    setPosting(true)
    try {
      await updateJournalEntryLines(activeDraftId, draftLines)
      await postJournalEntry(activeDraftId)
      await refreshDrafts(activeDraftId)
      toast.success('Journal entry posted to QuickBooks')
      await refreshAll({ silent: true })
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setPosting(false)
    }
  }

  const uploadsSyncTooltip = coverage?.halo_bills_synced_at
    ? `Last synced ${new Date(coverage.halo_bills_synced_at).toLocaleString()}`
    : 'Not synced yet — pull QBO bills from QuickBooks to compare.'

  const uploadsSyncAction = (
    <button
      type="button"
      className="btn btn-secondary h-8 shrink-0 px-3 text-xs"
      onClick={() => void handleSyncQboBills()}
      disabled={loading}
      title={uploadsSyncTooltip}
    >
      Sync QBO bills
    </button>
  )

  return (
    <AccountingToolPage
      title="Warehouse"
      description="Track stock in the warehouse, record store shipments, and post to QuickBooks."
      scrollContent={activeTab !== 'inventory'}
      headerExtra={
        <WarehouseTabNav
          activeTab={activeTab}
          onSelect={(tab) => setActiveTab(tab)}
          badges={tabBadges}
        />
      }
    >

      {activeTab === 'files' ? (
        <div className="space-y-4">
          <WarehouseUploadsPanel
            loading={loading}
            invoices={invoices}
            billingLines={billingLines}
            awareness={billingAwareness}
            catalogSkuOptions={skuCrosswalkRows.filter((row) => row.is_active)}
            selectedMonths={uploadsSelectedMonths}
            onSelectedMonthsChange={setUploadsSelectedMonths}
            monthOptions={uploadsMonthOptions}
            syncAction={uploadsSyncAction}
            containerErrorCounts={{
              wh: uploadsErrorCounts.wh,
              product: uploadsErrorCounts.product,
              billing: uploadsErrorCounts.billing,
            }}
            focusProductInvoiceLineRequest={focusProductInvoiceLineRequest}
            onFocusProductInvoiceHandled={() => setFocusProductInvoiceLineRequest(null)}
            onRefresh={async () => {
              await refreshAll({ silent: true })
            }}
            onOpenCatalogSku={handleOpenCatalogSku}
            onJumpToLedgerTxn={handleJumpToLedgerTxn}
            onInvoiceFieldCommit={handleInvoiceFieldCommit}
            onDeleteInvoice={handleDeleteInvoice}
            onDeleteBillingMonth={handleDeleteBillingMonth}
          />
          {loading && !coverage ? (
            <LoadingPlaceholder message="Loading…" />
          ) : coverage ? (
            <div className="space-y-5">
                {orphanAdjustments.length > 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <p className="font-medium">Manual adjustments without a billing file</p>
                    <p className="mt-1 text-xs text-amber-800">
                      These do not match any uploaded billing package. They were likely created while
                      testing the old adjustment form and can be deleted safely if you do not need
                      them.
                    </p>
                    <ul className="mt-3 space-y-2 text-xs">
                      {orphanAdjustments.map((adjustment) => (
                        <li
                          key={adjustment.id}
                          className="flex flex-wrap items-center justify-between gap-2"
                        >
                          <span>
                            {adjustment.report_month} · {adjustment.adjustment_type} ·{' '}
                            {formatMoney(adjustment.amount)}
                          </span>
                          <button
                            type="button"
                            className="inline-flex h-9 w-9 items-center justify-center rounded border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            aria-label={`Delete adjustment for ${adjustment.report_month}`}
                            onClick={() => void handleDeleteAdjustment(adjustment.id)}
                            disabled={loading}
                          >
                            <MinusIcon className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {/* Step 4 — QBO bills */}
                <section
                  id="qbo-bill-reconciliation"
                  className="rounded-xl border border-gray-200 bg-white p-4 md:p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="container-title">
                          <HoverHelp label="QBO bills" aria-label="QBO bills check help">
                            {QBO_CHECK_HELP}
                          </HoverHelp>
                        </h2>
                        <AlertCountBadge
                          count={uploadsErrorCounts.qbo}
                          label={`${uploadsErrorCounts.qbo} error${uploadsErrorCounts.qbo === 1 ? '' : 's'}`}
                          variant="section"
                        />
                      </div>
                      {!check3NeedsSync && filteredQboMatches.length > 0 ? (
                        <p className="mt-1 text-xs text-gray-500">
                          {qboMatched.length} matched · {qboMismatches.length} need review
                        </p>
                      ) : null}
                      <p className="mt-1 text-xs text-gray-500">
                        Match each uploaded invoice to its QuickBooks vendor bill (same vendor and
                        Doc #). BK product and WH service bills should show Holding &gt; $0 in the
                        cache. Direct-to-store bills are not synced (Holding $0) — use Ignore if
                        they show as missing.
                      </p>
                    </div>
                  </div>

                  {check3NeedsSync && allQboMatches.length === 0 ? (
                    <p className="mt-4 text-sm text-gray-500">
                      Sync QBO bills from QuickBooks to compare them against uploaded invoices.
                    </p>
                  ) : filteredQboMatches.length === 0 ? (
                    <p className="mt-4 text-sm text-gray-500">
                      No QuickBooks bills for the selected month(s).
                    </p>
                  ) : (
                    <div className="mt-4 space-y-3">
                      <div className="overflow-hidden rounded-lg border border-gray-100">
                        <div className="table-scroll-mobile overflow-x-auto">
                          <table className="table-forecast border-separate border-spacing-0 min-w-max">
                            <thead>
                              <tr>
                                <SortableHeader
                                  label="Status"
                                  sortKey="status"
                                  sortDirection={getQboSortDirection('status')}
                                  onSort={requestQboSort}
                                />
                                <SortableHeader
                                  label="Doc #"
                                  sortKey="doc_number"
                                  sortDirection={getQboSortDirection('doc_number')}
                                  onSort={requestQboSort}
                                />
                                <SortableHeader
                                  label="Vendor"
                                  sortKey="vendor_name"
                                  sortDirection={getQboSortDirection('vendor_name')}
                                  onSort={requestQboSort}
                                />
                                <SortableHeader
                                  label="Date"
                                  sortKey="txn_date"
                                  sortDirection={getQboSortDirection('txn_date')}
                                  onSort={requestQboSort}
                                />
                                <SortableHeader
                                  label={
                                    <HoverHelp
                                      label="Type"
                                      aria-label="Invoice type help"
                                    >
                                      {HALO_INVOICE_TYPE_HELP}
                                    </HoverHelp>
                                  }
                                  sortKey="invoice_type"
                                  sortDirection={getQboSortDirection('invoice_type')}
                                  onSort={requestQboSort}
                                />
                                <SortableHeader
                                  label="QBO bill"
                                  sortKey="bill_total"
                                  sortDirection={getQboSortDirection('bill_total')}
                                  onSort={requestQboSort}
                                />
                                <SortableHeader
                                  label="Uploaded invoice"
                                  sortKey="invoice_total"
                                  sortDirection={getQboSortDirection('invoice_total')}
                                  onSort={requestQboSort}
                                />
                                <SortableHeader
                                  label={
                                    <HoverHelp
                                      label="Holding"
                                      aria-label="3PL Holding column help"
                                    >
                                      {QBO_HOLDING_COLUMN_HELP}
                                    </HoverHelp>
                                  }
                                  sortKey="holding_amount"
                                  sortDirection={getQboSortDirection('holding_amount')}
                                  onSort={requestQboSort}
                                />
                                <th>Actions</th>
                                <th>Note</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedQboDisplayRows.map((match: WarehouseQboBillMatch) => {
                                const rowMonth = qboRowReconcileMonth(
                                  match,
                                  selectedReconcileMonth
                                )
                                const actionIgnore = getActiveReconcileIgnore(
                                  match.reconcile_ignores,
                                  rowMonth
                                )
                                const statusBadge = qboMatchStatusBadge(match, rowMonth)
                                return (
                                <tr
                                  key={`${match.vendor_id ?? 'na'}-${match.doc_number}`}
                                  className={qboMatchRowClass(match, rowMonth)}
                                >
                                  <td>
                                    <HoverHelp
                                      label={
                                        <span
                                          className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ${statusBadge.className}`}
                                        >
                                          {statusBadge.label}
                                        </span>
                                      }
                                      aria-label={`${statusBadge.label} status`}
                                      className="border-b-0"
                                    >
                                      <p>{statusBadge.tooltip}</p>
                                    </HoverHelp>
                                  </td>
                                  <td>{match.doc_number}</td>
                                  <td>{match.vendor_name ?? '—'}</td>
                                  <td>{match.txn_date ?? '—'}</td>
                                  <td>
                                    {match.invoice_type
                                      ? INVOICE_TYPE_LABEL_BY_ID[match.invoice_type] ??
                                        match.invoice_type
                                      : '—'}
                                  </td>
                                  <td>{formatMoney(match.bill_total)}</td>
                                  <td>{formatMoney(match.invoice_total)}</td>
                                  <td>{formatMoney(match.holding_amount)}</td>
                                  <td className="min-w-[6.5rem] whitespace-nowrap">
                                    <div className="flex flex-wrap items-center gap-1">
                                      {actionIgnore ? (
                                        <button
                                          type="button"
                                          className="btn btn-secondary h-7 px-2.5 text-xs"
                                          disabled={reconcileRefreshing}
                                          onClick={() =>
                                            void handleReconcileUnignore(actionIgnore.id)
                                          }
                                        >
                                          Undo ignore
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="btn btn-secondary h-7 px-2.5 text-xs"
                                          disabled={reconcileRefreshing || !rowMonth}
                                          onClick={() =>
                                            void handleReconcileIgnore({
                                              target_type: 'qbo_bill',
                                              doc_number: match.doc_number,
                                              report_month: rowMonth ?? undefined,
                                            })
                                          }
                                        >
                                          Ignore
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="min-w-[18rem]">
                                    <ReconcileNoteField
                                      systemNote={match.note}
                                      userNote={reconcileSavedNote(
                                        match.user_notes_by_month,
                                        rowMonth ?? ''
                                      )}
                                      disabled={reconcileRefreshing || !rowMonth}
                                      onCommit={(note) =>
                                        handleReconcileNoteSave(
                                          {
                                            target_type: 'qbo_bill',
                                            doc_number: match.doc_number,
                                            report_month: rowMonth ?? undefined,
                                          },
                                          note
                                        )
                                      }
                                    />
                                  </td>
                                </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {qboIgnoredRows.length > 0 ? (
                        <button
                          type="button"
                          className="text-xs font-medium text-brand-700 hover:underline"
                          onClick={() => setShowIgnoredQbo((open) => !open)}
                        >
                          {showIgnoredQbo
                            ? 'Hide ignored'
                            : `Show ignored (${qboIgnoredRows.length})`}
                        </button>
                      ) : null}
                    </div>
                  )}
                </section>
          </div>
          ) : null}
        </div>
      ) : null}

      {activeTab === 'inventory' ? (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <WarehouseInventoryWorkspace
            availableMonths={sortedReconcileMonths}
            formatMonthLabel={reportMonthLabel}
            layers={layers}
            movements={movements}
            invoices={invoices}
            bkMatches={coverage?.bk_matches ?? []}
            skuDisplayNames={skuDisplayNames}
            manualEntries={manualEntries}
            pendingKeys={pendingProgressionKeys}
            viewMode={inventoryViewMode}
            onViewModeChange={setInventoryViewMode}
            catalogSkuFilterRequest={catalogSkuFilterRequest}
            ledgerBkTxnFilterRequest={ledgerBkTxnFilterRequest}
            onConfirmPending={confirmPendingProgressionKeys}
            onSkuCrosswalkUpdated={setSkuCrosswalkRows}
            onSkuCrosswalkSaved={handleSkuCrosswalkSaved}
            busy={loading}
            brightSitesAuthRecoveryHint={authRecovery}
            onBrightSitesAuthError={() => setAuthRecovery(true)}
            onBrightSitesApplied={async () => {
              await refreshAll({ silent: true })
            }}
            onJumpToProductInvoice={handleJumpToProductInvoice}
          />
        </div>
      ) : null}

      {activeTab === 'books' ? (
        <div className="flex flex-col gap-4">
          <PanelCard
            title="Journal entries"
            description="Create and post QuickBooks entries from the warehouse ledger and uploaded billing files."
            helpStyle="hover"
            helpContent={
              <p>
                Draft journal entries for manufacturer stock, initial store transfers, monthly
                warehouse costs/fees, and adjustments. Review lines here before posting to
                QuickBooks.
              </p>
            }
            headerAction={
              processMonthHasJeDrafts ? null : (
                <button
                  type="button"
                  className="btn btn-primary text-sm"
                  onClick={() => void handleCreateJeDrafts()}
                  disabled={loading || draftsLoading || !canCreateJeDrafts}
                >
                  {loading ? 'Creating…' : 'Create JE Drafts'}
                </button>
              )
            }
            className="shrink-0"
            contentClassName="flex flex-col px-5 py-4"
          >
            <div className="flex shrink-0 flex-wrap items-end gap-3">
              <div>
                <label
                  htmlFor="warehouse-books-month"
                  className="mb-1.5 block text-xs font-medium text-gray-600"
                >
                  Process month
                </label>
                <ReportMonthSelect
                  id="warehouse-books-month"
                  options={processMonthOptions}
                  value={processMonth || null}
                  onChange={(id) => setSelectedMonth(id ?? '')}
                  disabled={sortedReconcileMonths.length === 0}
                  placeholder="No billing months"
                  className="min-w-[10rem]"
                />
              </div>
            </div>

            {!processMonth ? (
              <p className="mt-4 shrink-0 text-xs text-amber-800">
                Select a process month after uploading billing on Uploads.
              </p>
            ) : !processMonthReconcileReady ? (
              <p className="mt-4 shrink-0 text-xs text-amber-800">
                Finish WH fee invoice amounts on Uploads (Check 1) before creating warehouse activity journal
                drafts.
              </p>
            ) : processMonth && !processMonthFeeTotals.hasWhInvoice ? (
              <p className="mt-4 shrink-0 text-xs text-amber-800">
                No fee invoice found for {reportMonthLabel(processMonth)}. Storage, admin, and
                shipping fees will be zero unless you upload the invoice on Uploads.
              </p>
            ) : null}

            {draftsLoading && drafts.length === 0 ? (
              <p className="mt-4 shrink-0 text-sm text-gray-500">Loading drafts…</p>
            ) : null}

            {processMonth && (readinessLoading || jeReadiness) ? (
              <div className="mt-4 flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                <button
                  type="button"
                  className="flex shrink-0 items-center gap-2 px-3 py-2 text-left"
                  onClick={() => setReadinessChecklistCollapsed((prev) => !prev)}
                  aria-expanded={!readinessChecklistCollapsed}
                >
                  {readinessChecklistCollapsed ? (
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                  )}
                  <span className="text-xs font-medium text-gray-700">Ledger readiness checklist</span>
                  {jeReadiness ? (
                    <span className="text-[11px] text-gray-500">
                      {jeReadiness.pass_count} pass · {jeReadiness.warn_count} warn ·{' '}
                      {jeReadiness.block_count} block
                    </span>
                  ) : null}
                </button>
                {!readinessChecklistCollapsed ? (
                  <div className="flex flex-col px-3 pb-3">
                    {readinessLoading ? (
                      <p className="mt-1 text-xs text-gray-500">Loading readiness checks…</p>
                    ) : jeReadiness && sortedReadinessItems.length > 0 ? (
                      <ul className="mt-1 max-h-[500px] space-y-1.5 overflow-y-auto">
                        {sortedReadinessItems.map((item) => (
                          <li
                            key={`${item.code}-${item.txn_id ?? item.message}`}
                            className={`rounded border px-2.5 py-1.5 text-xs ${readinessLevelClass(item.level)}`}
                          >
                            <span className="font-medium uppercase">{item.level}</span>
                            <span className="mx-1.5 text-gray-400">·</span>
                            {item.bk_txn_id ? (
                              <>
                                <span>{item.bk_txn_id.slice(0, 8)}</span>
                                <span className="mx-1.5 text-gray-400">·</span>
                              </>
                            ) : null}
                            {item.message}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        No readiness findings for this month.
                      </p>
                    )}
                    {jeReadiness && jeReadiness.block_count > 0 ? (
                      <p className="mt-2 shrink-0 text-xs text-red-800">
                        Resolve blocking items on Inventory or Uploads before creating drafts.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </PanelCard>

          <UnderlineTabNav
            className="shrink-0"
            ariaLabel="Journal entry type"
            tabs={[
              { id: 'bk', label: 'Backstock purchases' },
              { id: 'month', label: 'Warehouse activity' },
            ]}
            activeId={jeSegment}
            onChange={(id) => setJeSegment(id as JeSegment)}
          />

          {(activeDraft || legacyMonthlyView) && draftLines.length > 0 ? (
            <JournalEntryCard
              className="w-full"
              posted={activeDraft?.status === 'posted'}
              title={jeSegment === 'bk' ? 'Backstock purchases' : 'Warehouse activity'}
              identity={
                <>
                  {activeDraft?.doc_number ? (
                    <JournalEntryDocChip>{activeDraft.doc_number}</JournalEntryDocChip>
                  ) : null}
                  {activeDraftTxnDateLabel ? <span>{activeDraftTxnDateLabel}</span> : null}
                  {activeDraft?.status === 'posted' && activeDraft.qbo_url ? (
                    <JournalEntryExternalLink href={activeDraft.qbo_url}>
                      View in QuickBooks
                    </JournalEntryExternalLink>
                  ) : null}
                </>
              }
              actions={
                <>
                  {activeDraft?.status === 'posted' ? (
                    <JournalEntryPostedBadge
                      loading={draftActionId === activeDraft.id}
                      menu={
                        <>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={draftActionId === activeDraft.id}
                            onClick={() => {
                              setPostedJeMenuOpen(false)
                              void handleRegeneratePostedDraft(activeDraft.id)
                            }}
                          >
                            Regenerate
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={draftActionId === activeDraft.id}
                            onClick={() => {
                              setPostedJeMenuOpen(false)
                              void handlePushQboUpdate(activeDraft.id)
                            }}
                          >
                            Update Existing JE
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={draftActionId === activeDraft.id}
                            onClick={() => {
                              setPostedJeMenuOpen(false)
                              void handleDeleteFromQbo(activeDraft.id)
                            }}
                          >
                            Delete Existing JE
                          </button>
                        </>
                      }
                      menuOpen={postedJeMenuOpen}
                      onMenuOpenChange={setPostedJeMenuOpen}
                    />
                  ) : !legacyMonthlyView && activeDraft?.status === 'draft' ? (
                    <JournalEntryPostSplitButton
                      primaryLabel={posting ? 'Posting…' : 'Post to QuickBooks'}
                      primaryDisabled={posting || !balanced}
                      primaryDisabledTitle={
                        !balanced ? 'Journal entry is out of balance' : undefined
                      }
                      menuDisabled={draftActionId === activeDraft.id}
                      onPrimaryClick={() => void handlePostDraft()}
                      menuOpen={draftJeMenuOpen}
                      onMenuOpenChange={setDraftJeMenuOpen}
                      menu={
                        <button
                          type="button"
                          role="menuitem"
                          disabled={draftActionId === activeDraft.id}
                          onClick={() => {
                            setDraftJeMenuOpen(false)
                            void handleRegenerateDraft(activeDraft.id)
                          }}
                        >
                          {draftActionId === activeDraft.id ? 'Regenerating…' : 'Regenerate'}
                        </button>
                      }
                    />
                  ) : null}
                </>
              }
              alerts={
                legacyMonthlyView ||
                (activeDraft && isDraftOutOfDateVsLedger(activeDraft)) ||
                (activeDraft?.status === 'draft' && !balanced) ||
                (activeDraft?.warnings ?? []).length > 0 ||
                activeDraft?.error_message
                  ? (
                      <>
                        {legacyMonthlyView ? (
                          <WarningBanner message="These are legacy separate warehouse activity drafts. Click Create JE Drafts to rebuild them as one combined journal entry before posting." />
                        ) : null}
                        {activeDraft && isDraftOutOfDateVsLedger(activeDraft) ? (
                          <WarningBanner message="This draft is out of date vs the current ledger. Regenerate before posting, or use Update Existing JE on the Posted control if already posted." />
                        ) : null}
                        {activeDraft?.status === 'draft' && !balanced ? (
                          <WarningBanner message="Journal entry is out of balance. Adjust lines before posting." />
                        ) : null}
                        {(activeDraft?.warnings ?? []).map((warning, index) => (
                          <WarningBanner key={index} message={warning} />
                        ))}
                        {activeDraft?.error_message ? (
                          <WarningBanner message={activeDraft.error_message} />
                        ) : null}
                      </>
                    )
                  : undefined
              }
            >
              <JournalEntryPreview
                lines={draftLines}
                lineSections={previewLineSections}
                variant="split"
                hideHeader
                editableAmounts={activeDraft?.status === 'draft' && !legacyMonthlyView}
                editableLines={activeDraft?.status === 'draft' && !legacyMonthlyView}
                onLinesChange={
                  activeDraft?.status === 'draft' && !legacyMonthlyView && !posting
                    ? setDraftLines
                    : undefined
                }
              />
            </JournalEntryCard>
          ) : processMonth && !draftsLoading ? (
            <p className="text-sm text-gray-500">
              No {jeSegment === 'bk' ? 'backstock' : 'warehouse activity'} journal entry drafts for{' '}
              {reportMonthLabel(processMonth)}. Click Create JE Drafts to generate them.
            </p>
          ) : null}

        </div>
      ) : null}

      {activeTab === 'warehouse_settings' ? (
        <div className="space-y-4">
          <CredentialStatusCard
            service="brightsites"
            authRecoveryHint={authRecovery}
            onGrabSuccess={() => void refreshAll()}
            showSettingsLink={false}
            showLastUpdated={false}
            secondaryAction={{
              label: 'Refresh Report Data',
              onClick: () => void handleRefresh(),
              disabled: loading,
              loading,
              title: 'Re-import the Bright Sites custom report and refresh all warehouse data',
            }}
          />
        </div>
      ) : null}
    </AccountingToolPage>
  )
}
